import { FC } from 'react';

import Image from 'next/image';

import classNames from 'classnames';

import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';

import { TypeProduct } from '@/core/generated-types/TypeProduct';

import interpolate from '@/utils/translations/interpolate';
import { TypeAccountERCPageTranslations } from '@/utils/translations/types/TypeAccountERCPageTranslations';

import { useAccordion } from '@/utils/useAccordion';

import { ShipDate } from './components';

export interface ProductInfoProps {
  t: TypeAccountERCPageTranslations;
  product: TypeProduct;
  shipDate?: string;
  onlyCollapsed?: boolean;
  isCurrentBox?: boolean;
  isNextOrder?: boolean;
  isUpcomingBox?: boolean;
}

const ProductInfo: FC<ProductInfoProps> = ({
  t,
  product,
  shipDate,
  onlyCollapsed,
  isCurrentBox = false,
  isNextOrder = false,
  isUpcomingBox = false,
}) => {
  const { Icon, Accordion } = useAccordion();

  const CollapsedContent = () => (
    <div
      className={classNames(
        'grid gap-x-4 mb:gap-x-6 md:items-center mb-6 md:mb-8',
        {
          'md:grid-cols-[auto_1fr]': isCurrentBox,
          'grid-cols-[auto_1fr]': isNextOrder || isUpcomingBox,
        },
      )}
    >
      <Image
        className={classNames({
          'w-full md:w-64': isCurrentBox,
          'size-[88px] md:size-[140px]': isNextOrder || isUpcomingBox,
        })}
        width={140}
        height={140}
        src={`https:${product.fields.featuredImage.fields.Image.fields.file.url}`}
        alt={product.fields.title}
      />

      <div className="grid grid-cols-[1fr_auto] gap-x-4 mb:gap-x-6">
        <div className="grid gap-y-1">
          {!isCurrentBox && shipDate && (
            <ShipDate
              shipDateText={t.shipDate}
              editText={t.edit}
              scheduledDate={shipDate}
              canEdit={isNextOrder}
            />
          )}

          <div className="text-left">
            <HeaderText
              classes="text-h-x4 md:text-h-x3"
              omitSizeString
              size={HeaderSize.H3}
              text={product.fields.title}
            />

            <p className="text-sm md:text-base">
              {product.fields.shortDescription}
            </p>
          </div>
        </div>

        {!onlyCollapsed && (
          <div className="flex items-center">
            <Icon />
          </div>
        )}
      </div>
    </div>
  );

  const ExpandedContent = () => (
    <div className="grid gap-y-4 mb-6 md:mb-8">
      <div className="grid gap-y-4">
        <HeaderText
          classes="text-base md:text-h-x5"
          omitSizeString
          size={HeaderSize.H4}
          text={interpolate(t.insideTitle, {
            shortDescription:
              product.fields.shortDescription?.toLowerCase() || '',
          })}
        />

        <div className="flex flex-row gap-x-2 items-start overflow-x-auto scroll-smooth">
          {product.fields.subProducts?.map((subProduct, index) => (
            <div
              key={`sub-product-${index}`}
              className="grid gap-y-2 w-[88px] md:w-[104px] min-w-[88px] md:min-w-[104px]"
            >
              <Image
                className="size-[88px] md:size-[104px]"
                width={104}
                height={104}
                src={`https:${subProduct.fields.image.fields.file.url}`}
                alt={subProduct.fields.title}
              />

              <p className="text-xs font-bold">{subProduct.fields.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (onlyCollapsed) {
    return (
      <div data-testid="productInfo-onlyCollapsed">
        <CollapsedContent />
      </div>
    );
  }

  return (
    <div data-testid="productInfo">
      <Accordion
        collapsedContent={<CollapsedContent />}
        expandedContent={<ExpandedContent />}
      />
    </div>
  );
};

export default ProductInfo;
